import { useIntl } from 'react-intl';
import clsx from 'clsx';
import ReactFocusLock from 'react-focus-lock';

import { Select } from 'components/select';
import { Button } from 'components/button';
import { RadioButtonGroup } from 'components/radio-button-group/radio-button-group';
import { Icon } from 'components/icon/icon';
import { RadioButtonItem } from 'components/core/radio-button-group';
import CrossIcon from 'public/assets/icons/cross.svg';

import styles from './sort.module.scss';
import { useUncontrolled } from 'utils/hooks/use-uncontrolled';

export interface ISortOption {
	id: string;
	label: string;
	shortLabel?: string;
}

export interface ISortProps {
	mobileIsOpen: boolean;
	setMobileIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onChange: (selectedSortFilter: { id: string; label: string } | undefined) => void;
	customOptions?: ISortOption[];
	stateType?: 'subsidy' | 'wobRequests' | 'wooRequests' | 'mapsFigures' | 'permits' | 'search';
	value?: ISortOption;
}

export const Sort = ({
	mobileIsOpen,
	setMobileIsOpen,
	onChange,
	customOptions,
	value
}: ISortProps): React.JSX.Element => {
	const intl = useIntl();
	const sortFilterOptions = customOptions || [
		{
			id: 'title:asc',
			label: intl.formatMessage({ id: 'global.sortAToZ' }),
			shortLabel: intl.formatMessage({ id: 'global.aToZ' })
		},
		{
			id: 'title:desc',
			label: intl.formatMessage({ id: 'global.sortZToA' }),
			shortLabel: intl.formatMessage({ id: 'global.zToA' })
		}
	];

	const [_value, _onChange] = useUncontrolled<ISortOption>({
		defaultValue: sortFilterOptions[0],
		onChange,
		value
	});

	const onSelectChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
		const selectedSortFilter = sortFilterOptions.find((filter) => filter.id === event.target.value);
		if (selectedSortFilter) {
			_onChange(selectedSortFilter);
		}
	};

	const onRadioChange = (item: RadioButtonItem): void => {
		const selectedSortFilter = sortFilterOptions.find((filter) => filter.id === item.id);
		if (selectedSortFilter) {
			_onChange(selectedSortFilter);
		}
	};

	const onEnter = (event: React.KeyboardEvent<HTMLElement>): void => {
		if (event.key === 'Enter') {
			setMobileIsOpen(false);
		}
	};

	const onShowResults = (): void => {
		onChange(_value);
		setMobileIsOpen(false);
	};

	return (
		<>
			{/* mobile */}
			<ReactFocusLock
				disabled={!mobileIsOpen}
				autoFocus={mobileIsOpen}
				className={clsx(styles.sort, mobileIsOpen && styles.sortOpen)}
			>
				<div className={styles.sortHeader}>
					<h3 id={intl.formatMessage({ id: 'global.sort' })}>
						{intl.formatMessage({ id: 'global.sort' })}
						<span
							role="button"
							onClick={() => setMobileIsOpen(false)}
							onKeyUp={onEnter}
							tabIndex={0}
							aria-label={intl.formatMessage({ id: 'global.closeSort' })}
						>
							<Icon customIcon={<CrossIcon />} size="14px" />
						</span>
					</h3>
				</div>

				<div className={clsx(styles.sortBody, styles.sortGroup, styles.sortGroupActive)}>
					<RadioButtonGroup
						id="sort-radio"
						items={sortFilterOptions.map((item) => ({ id: item.id, label: item.shortLabel || item.label }))}
						onChange={onRadioChange}
						selectedItem={_value as RadioButtonItem}
						singleColumn
					/>
				</div>

				<div className={styles.sortFooter}>
					<Button title={intl.formatMessage({ id: 'global.showResults' })} onClick={onShowResults} fullWidth />
				</div>
			</ReactFocusLock>
			{/* desktop */}
			<span className={styles.desktopSelect}>
				<Select
					id="sort"
					options={sortFilterOptions}
					onChange={onSelectChange}
					value={_value?.id}
					variant="filter"
					name={intl.formatMessage({ id: 'global.sort' })}
				/>
			</span>
		</>
	);
};

export default Sort;
